import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { LogOut, Trash2, RefreshCw, Download } from "lucide-react";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "active_admin_token";
const STATUSES = ["new", "contacted", "qualified", "won", "lost"];

const STATUS_COLORS = {
  new: "bg-signal/10 text-signal border-signal/30",
  contacted: "bg-amber-50 text-amber-700 border-amber-200",
  qualified: "bg-emerald-50 text-emerald-700 border-emerald-200",
  won: "bg-emerald-600 text-white border-emerald-600",
  lost: "bg-slate-100 text-slate-500 border-slate-200",
};

function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

function Login({ onLogin }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const { data } = await axios.post(`${API}/auth/login`, { email, password });
      localStorage.setItem(TOKEN_KEY, data.token);
      onLogin(data.token);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-[80vh] flex items-center justify-center px-4 pt-16" data-testid="admin-login">
      <motion.form
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
        onSubmit={submit}
        data-testid="admin-login-form"
        className="w-full max-w-sm rounded-2xl border border-slate-200 bg-white p-8 shadow-[0_8px_40px_rgba(15,23,42,0.06)]"
      >
        <div className="flex items-center gap-2.5 mb-8">
          <img src="/brand/wordmark.png" alt="ACTIVE" className="h-[18px] w-auto" />
          <span className="font-mono text-[10px] tracking-[0.3em] text-slate-400 uppercase">Admin</span>
        </div>
        <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500 block mb-1.5">Email</label>
        <Input
          type="email"
          required
          data-testid="admin-email-input"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          className="h-11 rounded-lg border-slate-200 mb-4"
          placeholder="admin@active-stairlifts.com"
        />
        <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500 block mb-1.5">Password</label>
        <Input
          type="password"
          required
          data-testid="admin-password-input"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          className="h-11 rounded-lg border-slate-200"
          placeholder="••••••••"
        />
        {error && (
          <p data-testid="admin-login-error" className="mt-4 text-sm text-red-600">{error}</p>
        )}
        <button
          type="submit"
          disabled={loading}
          data-testid="admin-login-submit"
          className="mt-6 w-full rounded-full bg-obsidian text-white text-sm font-medium px-6 py-3.5 hover:bg-signal transition-colors duration-300 disabled:opacity-50"
        >
          {loading ? "Signing in..." : "Sign In"}
        </button>
      </motion.form>
    </div>
  );
}

function Dashboard({ token, onLogout }) {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("all");
  const [typeFilter, setTypeFilter] = useState("all");
  const auth = { headers: { Authorization: `Bearer ${token}` } };

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/admin/leads`, auth);
      setLeads(data);
    } catch (err) {
      if (err.response?.status === 401) onLogout();
      else toast.error("Could not load leads.");
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const setStatus = async (id, status) => {
    try {
      await axios.patch(`${API}/admin/leads/${id}`, { status }, auth);
      setLeads((ls) => ls.map((l) => (l.id === id ? { ...l, status } : l)));
      toast.success(`Status updated to ${status}.`);
    } catch {
      toast.error("Status update failed.");
    }
  };

  const remove = async (id, company) => {
    try {
      await axios.delete(`${API}/admin/leads/${id}`, auth);
      setLeads((ls) => ls.filter((l) => l.id !== id));
      toast.success(`${company} removed.`);
    } catch {
      toast.error("Delete failed.");
    }
  };

  const filtered = leads.filter(
    (l) => (statusFilter === "all" || l.status === statusFilter) && (typeFilter === "all" || l.type === typeFilter)
  );

  const exportCsv = () => {
    const cols = ["id", "type", "status", "company", "name", "email", "phone", "country", "brands", "installations", "volume", "context", "message", "created_at"];
    const esc = (v) => `"${String(v ?? "").replace(/"/g, '""').replace(/\r?\n/g, " ")}"`;
    const rows = filtered.map((l) => cols.map((c) => esc(l[c])).join(","));
    const csv = "\uFEFF" + cols.join(",") + "\n" + rows.join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `active-leads-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
    toast.success(`${filtered.length} lead${filtered.length === 1 ? "" : "s"} exported.`);
  };
  const types = [...new Set(leads.map((l) => l.type))];
  const newCount = leads.filter((l) => l.status === "new").length;

  return (
    <div className="min-h-screen bg-[#F8F9FA] pt-28 pb-20" data-testid="admin-dashboard">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-slate-400">Lead Portal</p>
            <h1 className="mt-2 font-display text-3xl font-bold tracking-tighter text-slate-950">Enquiries</h1>
          </div>
          <div className="flex gap-3">
            <button
              data-testid="admin-export-csv-btn"
              onClick={exportCsv}
              className="inline-flex items-center gap-2 rounded-full border border-slate-300 text-slate-700 text-[13px] font-medium px-5 py-2.5 hover:border-slate-950 transition-colors"
            >
              <Download size={14} /> Export CSV
            </button>
            <button
              data-testid="admin-refresh-btn"
              onClick={() => { setLoading(true); load(); }}
              className="inline-flex items-center gap-2 rounded-full border border-slate-300 text-slate-700 text-[13px] font-medium px-5 py-2.5 hover:border-slate-950 transition-colors"
            >
              <RefreshCw size={14} /> Refresh
            </button>
            <button
              data-testid="admin-logout-btn"
              onClick={onLogout}
              className="inline-flex items-center gap-2 rounded-full bg-obsidian text-white text-[13px] font-medium px-5 py-2.5 hover:bg-signal transition-colors"
            >
              <LogOut size={14} /> Logout
            </button>
          </div>
        </div>

        <div className="mt-8 grid grid-cols-2 lg:grid-cols-4 gap-4">
          {[
            { label: "Total leads", value: leads.length },
            { label: "New", value: newCount },
            { label: "Distributors", value: leads.filter((l) => l.type === "distributor").length },
            { label: "OEM / ODM", value: leads.filter((l) => l.type === "oem" || l.type === "odm").length },
          ].map((s, i) => (
            <div key={s.label} className="rounded-2xl border border-slate-200/80 bg-white p-6" data-testid={`admin-stat-${i}`}>
              <p className="font-display text-3xl font-bold tracking-tighter text-slate-950">{s.value}</p>
              <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">{s.label}</p>
            </div>
          ))}
        </div>

        <div className="mt-8 flex flex-wrap gap-3">
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger data-testid="admin-filter-status" className="w-44 h-10 rounded-lg border-slate-200 bg-white">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              {STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={typeFilter} onValueChange={setTypeFilter}>
            <SelectTrigger data-testid="admin-filter-type" className="w-44 h-10 rounded-lg border-slate-200 bg-white">
              <SelectValue placeholder="Type" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All types</SelectItem>
              {types.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>

        <div className="mt-6 space-y-3" data-testid="admin-leads-list">
          {loading && <p className="text-sm text-slate-500 py-10 text-center">Loading leads...</p>}
          {!loading && filtered.length === 0 && (
            <p className="text-sm text-slate-500 py-10 text-center" data-testid="admin-empty">No leads match these filters.</p>
          )}
          {filtered.map((l) => (
            <motion.div
              key={l.id}
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4 }}
              className="rounded-2xl border border-slate-200/80 bg-white p-6"
              data-testid={`admin-lead-${l.id.slice(0, 8)}`}
            >
              <div className="flex flex-col lg:flex-row lg:items-start gap-5">
                <div className="flex-1 min-w-0">
                  <div className="flex flex-wrap items-center gap-3">
                    <h3 className="font-display text-lg font-semibold tracking-tight text-slate-950">{l.company}</h3>
                    <span className="font-mono text-[9px] uppercase tracking-[0.15em] text-signal border border-signal/30 rounded-full px-2 py-0.5">
                      {l.type}
                    </span>
                    <span className={`font-mono text-[9px] uppercase tracking-[0.15em] border rounded-full px-2 py-0.5 ${STATUS_COLORS[l.status] || STATUS_COLORS.new}`}>
                      {l.status || "new"}
                    </span>
                  </div>
                  <p className="mt-1.5 text-sm text-slate-600">
                    {l.name} · <a href={`mailto:${l.email}`} className="text-signal hover:underline">{l.email}</a>
                    {l.phone ? ` · ${l.phone}` : ""} {l.country ? ` · ${l.country}` : ""}
                  </p>
                  <div className="mt-2 flex flex-wrap gap-x-5 gap-y-1 font-mono text-[11px] text-slate-400">
                    {l.brands && <span>Brands: {l.brands}</span>}
                    {l.installations && <span>Installs/yr: {l.installations}</span>}
                    {l.volume && <span>Volume: {l.volume}</span>}
                    {l.context && <span>Source: {l.context}</span>}
                  </div>
                  {l.message && <p className="mt-2.5 text-sm text-slate-600 leading-relaxed">“{l.message}”</p>}
                  <p className="mt-2 font-mono text-[10px] text-slate-400">
                    {new Date(l.created_at).toLocaleString("en-GB", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" })}
                  </p>
                </div>
                <div className="flex lg:flex-col items-center gap-3 shrink-0">
                  <Select value={l.status || "new"} onValueChange={(v) => setStatus(l.id, v)}>
                    <SelectTrigger data-testid={`lead-status-${l.id.slice(0, 8)}`} className="w-36 h-9 rounded-lg border-slate-200 text-sm">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <button
                    data-testid={`lead-delete-${l.id.slice(0, 8)}`}
                    onClick={() => remove(l.id, l.company)}
                    className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 text-slate-400 text-[12px] px-4 py-2 hover:border-red-300 hover:text-red-600 transition-colors"
                  >
                    <Trash2 size={13} /> Delete
                  </button>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default function Admin() {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY));
  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setToken(null);
  };
  return token ? <Dashboard token={token} onLogout={logout} /> : <Login onLogin={setToken} />;
}
